/**
 * Hash routing for the Console's screens (ADR-103, ADR-113).
 *
 * §16.1 lists ten screens. Listing all ten in the sidebar, each under its spec
 * code, is what the first version did — and it made the Console unusable: eleven
 * equally-weighted destinations named after implementation concepts, with no
 * indication which one you need to open today. The spec numbers the screens; it
 * does not say the operator must learn the numbering.
 *
 * So the screens are the same and the *menu* is different. `section` decides
 * where each appears:
 *
 *  - `primary`  — the things an operator opens during normal work.
 *                 It was five, and two of those were the same screen twice:
 *                 Team and Agents both rendered one card per role, and History
 *                 asked "what happened to this task", which is a question about
 *                 a card and now lives on the card (ADR-118).
 *  - `settings` — configuration, grouped under Settings. Changed rarely, and
 *                 never while watching the team work.
 *  - `detail`   — reached by clicking something specific. A menu entry for
 *                 these would be a link to nothing in particular.
 *
 * The drawn office was removed (ADR-114). It showed rectangles standing in for
 * desks and could say nothing an operator could act on; the kanban board already
 * shows where work is, and the Agents screen shows what each agent is doing as
 * it does it.
 *
 * ADR-103 forbids a bundler and a framework, so there is no router dependency
 * either. A hash router is the one form of client routing that needs no server
 * rewrite rules, which matters for AC-028: a clean local install must work
 * without anyone configuring a web server.
 *
 * Parsing is pure and lives here so it can be tested without a DOM, and so an
 * unknown hash has exactly one defined behaviour instead of one per screen.
 */

export type ScreenId =
  | 'board'
  | 'epics'
  | 'operator'
  | 'overview'
  | 'proposals'
  | 'credentials'
  | 'connections'
  | 'webhooks'
  | 'runs'
  | 'agent'
  | 'team-editor'
  | 'epic-coordinator'
  | 'board-settings'
  | 'skills'
  | 'mcp-access'
  | 'usage'
  | 'quorums'
  | 'releases'
  | 'deployment';

/** Where a screen appears in the menu — see the note at the top of this file. */
export type ScreenSection = 'primary' | 'settings' | 'detail' | 'hidden';

export interface Route {
  readonly screen: ScreenId;
  /** Task binding id (S-03) or role instance id (S-04); `null` for list views. */
  readonly id: string | null;
  /** Query parameters after the path, e.g. `#/board?filter=stuck`. */
  readonly params: ReadonlyMap<string, string>;
}

export interface ScreenMeta {
  readonly id: ScreenId;
  /** §16.1 identifier. Kept for traceability; no longer shown in the menu. */
  readonly code: string;
  readonly title: string;
  /**
   * What the screen is for, in the words of someone using it.
   *
   * These used to read "Draft, validate, diff and activate a TeamVersion" — true,
   * and meaningless unless you had read the specification. A menu entry has to
   * answer "is this where I go?" before it answers anything else.
   */
  readonly purpose: string;
  readonly path: string;
  /** `true` when the screen takes a trailing identifier segment. */
  readonly takesId: boolean;
  readonly section: ScreenSection;
}

/** Menu order: where work starts, then who is doing it, then everything else. */
export const SCREENS: readonly ScreenMeta[] = [
  {
    id: 'board',
    code: 'S-00',
    title: 'Board',
    purpose: 'The cards the team works from',
    path: '/board',
    // `#/board/{externalItemId}` opens that card. A card is a thing you send
    // somebody a link to, and it carries its own history now (ADR-118).
    takesId: true,
    section: 'primary',
  },
  {
    id: 'epics',
    code: 'S-19',
    title: 'Epics',
    purpose: 'Plan and track larger initiatives',
    path: '/epics',
    takesId: true,
    section: 'primary',
  },
  {
    /*
     * The operator, as a destination rather than a drawer. A conversation
     * with the agent that runs the team is primary work, not a sidebar — and
     * a drawer can hold neither a history of chats nor a composer worth
     * recording a voice note into.
     */
    id: 'operator',
    code: 'S-13',
    title: 'Operator',
    purpose: 'Talk to the agent that runs this team',
    path: '/operator',
    // `#/operator/{chatId}` opens that conversation.
    takesId: true,
    section: 'primary',
  },
  {
    id: 'overview',
    code: 'S-02',
    title: 'Team',
    purpose: 'Who is doing what, live',
    path: '/overview',
    takesId: false,
    section: 'primary',
  },
  {
    id: 'proposals',
    code: 'S-20',
    title: 'Proposals',
    purpose: 'Review work and skills suggested by agents',
    path: '/proposals',
    takesId: false,
    section: 'primary',
  },
  {
    id: 'releases',
    code: 'S-18',
    title: 'Releases',
    purpose: 'Production releases and every deployment',
    path: '/releases',
    // `/releases/{releaseId}` is the production business record in depth.
    takesId: true,
    section: 'primary',
  },

  /* ---------------------------------------------------------- settings --- */

  {
    id: 'team-editor',
    code: 'S-05',
    title: 'Roles & team',
    purpose: 'Add agents and change what each one does',
    path: '/team-editor',
    takesId: false,
    section: 'settings',
  },
  {
    id: 'epic-coordinator',
    code: 'S-22',
    title: 'Epic Coordinator',
    purpose: 'Configure the Agent that plans epics and inspects product repositories',
    path: '/epic-coordinator',
    takesId: false,
    section: 'settings',
  },
  {
    id: 'board-settings',
    code: 'S-14',
    title: 'Board columns',
    purpose: 'The columns cards move through, in order',
    path: '/board-settings',
    takesId: false,
    section: 'settings',
  },
  {
    /*
     * Deliberately absent from the navigation: what an agent is told and what
     * it can reach are configured on the agent's own card in Roles & team.
     * The route stays alive for links and muscle memory; `hidden` keeps it
     * out of the menu.
     */
    id: 'skills',
    code: 'S-06',
    title: 'Skills',
    purpose: 'Read and edit what each agent knows, full screen',
    path: '/skills',
    takesId: false,
    section: 'settings',
  },
  {
    /*
     * Credentials sit beside the team editor rather than inside it, because a
     * token is not part of the versioned document and must never become part of
     * one: activating a team version would then mean approving a diff with a
     * secret in it (§13.2). Same screen area, different lifecycle.
     */
    id: 'credentials',
    code: 'S-11',
    title: 'Tokens & access',
    purpose: 'Set a token once, tick which agents may use it',
    path: '/credentials',
    takesId: false,
    section: 'settings',
  },
  {
    /*
     * Providers sits beside Tokens & access for the same reason Tokens does:
     * an account is not part of the versioned team document and must never
     * become part of one, because activating a version would then mean
     * approving a diff with a secret in it (§13.2). What *is* in the document
     * is which account each agent uses — a name, reviewable like every other
     * configuration choice.
     */
    id: 'connections',
    code: 'S-21',
    title: 'AI providers',
    purpose: 'The accounts your agents run on, and what each has spent',
    path: '/connections',
    takesId: false,
    section: 'settings',
  },
  {
    id: 'webhooks',
    code: 'S-20',
    title: 'Webhooks',
    purpose: 'Wake an agent from external events without one run per event',
    path: '/webhooks',
    takesId: false,
    section: 'settings',
  },
  {
    /*
     * The other direction of MCP: agents *inside* get servers from the team
     * document; an agent *outside* gets this control plane as an MCP server.
     * The screen only renders client configuration — the tokens it embeds are
     * the same ones this page already holds.
     */
    id: 'mcp-access',
    code: 'S-15',
    title: 'MCP access',
    purpose: 'Operate this platform from your own agent',
    path: '/mcp-access',
    takesId: false,
    section: 'settings',
  },
  {
    id: 'usage',
    code: 'S-16',
    title: 'Usage',
    purpose: 'Tokens and time, by model and role',
    path: '/usage',
    takesId: false,
    section: 'settings',
  },
  {
    id: 'quorums',
    code: 'S-17',
    title: 'Quorums',
    purpose: 'Reusable panels of independent opinions',
    path: '/quorums',
    takesId: false,
    section: 'settings',
  },


  /* ------------------------------------------------------------ detail --- */

  {
    id: 'deployment',
    code: 'S-19',
    title: 'Deployment',
    purpose: 'One immutable deployment and all of its attempts',
    path: '/deployments',
    takesId: true,
    section: 'detail',
  },
  {
    id: 'agent',
    code: 'S-04',
    title: 'Agent',
    purpose: 'One role in depth',
    path: '/agents',
    takesId: true,
    section: 'detail',
  },
  {
    /*
     * Still a screen, no longer a destination: the same timeline renders inside
     * the open card, which is where the question "what happened to this?" is
     * actually asked. This route is what a link to one task's history resolves
     * to when there is no card to open it on.
     */
    id: 'runs',
    code: 'S-03',
    title: 'Task history',
    purpose: 'Stages, attempts and evidence for one task',
    path: '/runs',
    takesId: true,
    section: 'detail',
  },
];

/** The menu, in order. */
export const PRIMARY_SCREENS = SCREENS.filter((screen) => screen.section === 'primary');
/** Grouped under Settings, in order. */
export const SETTINGS_SCREENS = SCREENS.filter((screen) => screen.section === 'settings');

export const DEFAULT_ROUTE: Route = { screen: 'board', id: null, params: new Map() };

function screenForPath(segment: string): ScreenMeta | null {
  return SCREENS.find((screen) => screen.path === `/${segment}`) ?? null;
}

/**
 * Parses `#/screen/id?key=value`.
 *
 * Unknown screens resolve to the board rather than throwing: a bad bookmark
 * should land somewhere useful, and the address bar still shows what was asked
 * for.
 */
export function parseRoute(hash: string): Route {
  const withoutHash = hash.startsWith('#') ? hash.slice(1) : hash;
  const [pathPart = '', queryPart = ''] = withoutHash.split('?', 2);
  const params = new Map<string, string>();
  if (queryPart.length > 0) {
    for (const pair of queryPart.split('&')) {
      if (pair.length === 0) continue;
      const separator = pair.indexOf('=');
      const key = separator === -1 ? pair : pair.slice(0, separator);
      const value = separator === -1 ? '' : pair.slice(separator + 1);
      params.set(decodeURIComponent(key), decodeURIComponent(value));
    }
  }

  const segments = pathPart.split('/').filter((segment) => segment.length > 0);
  const first = segments[0];
  if (first === undefined) {
    return { screen: DEFAULT_ROUTE.screen, id: null, params };
  }

  const screen = screenForPath(first);
  if (screen === null) {
    return { screen: DEFAULT_ROUTE.screen, id: null, params };
  }

  const id = screen.takesId ? (segments[1] ?? null) : null;
  return { screen: screen.id, id, params };
}

export function screenMeta(id: ScreenId): ScreenMeta {
  const found = SCREENS.find((screen) => screen.id === id);
  if (found === undefined) {
    throw new Error(`unknown screen ${id}`);
  }
  return found;
}

/** Builds a href. Params are sorted so that two equal routes produce one string. */
export function routeHref(screen: ScreenId, id: string | null = null, params?: ReadonlyMap<string, string>): string {
  const meta = screenMeta(screen);
  const path = id === null || !meta.takesId ? meta.path : `${meta.path}/${encodeURIComponent(id)}`;
  if (params === undefined || params.size === 0) {
    return `#${path}`;
  }
  const query = [...params.entries()]
    .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
  return `#${path}?${query}`;
}

/** True when two routes would render the same screen, ignoring parameters. */
export function sameScreen(left: Route, right: Route): boolean {
  return left.screen === right.screen && left.id === right.id;
}
